/**
 * useProgramDashboards — queries agent_profiles to determine which
 * program dashboards (GA, Agent, Partner) the current user can access.
 * Returns sidebar entries for approved/active programs.
 *
 * Public role URLs use the canonical `members.primary_handle` (Phase D).
 * Legacy `role_handles` has been retired — the member's primary handle is
 * the sole source of truth.
 */
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@bright-web-skeleton/auth';

export interface ProgramDashboardEntry {
  label: string;
  route: string;
  programType: 'connector' | 'general_agent' | 'agent' | 'partner';
  icon: 'crown' | 'shield' | 'handshake' | 'link';
  /** Approval status from agent_profiles.status */
  status?: string;
  /** Canonical handle (members.primary_handle) */
  handle?: string | null;
  /** Public role page URL (only when handle exists) */
  publicUrl?: string | null;
}

export function useProgramDashboards() {
  const { authUser } = useAuth();
  const [dashboards, setDashboards] = useState<ProgramDashboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authUser?.id) {
      // No auth user — clearing dashboards
      setDashboards([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      // Fetch program roles + canonical member handle in parallel.
      const [profilesRes, memberRes] = await Promise.all([
        supabase
          .from('agent_profiles')
          .select('program_type, status')
          .eq('user_id', authUser.id)
          .in('status', ['approved', 'active']),
        supabase
          .from('members')
          .select('primary_handle')
          .eq('user_id', authUser.id)
          .maybeSingle(),
      ]);
      const data = profilesRes.data;
      const error = profilesRes.error;
      const memberHandle =
        ((memberRes.data?.primary_handle as string | null) || '').trim().toLowerCase() ||
        null;
      // Canonical resolver: every role uses the member's primary_handle.
      const resolveHandle = (_programType: string): string | null => memberHandle;

      if (cancelled) return;

      if (error) {
        console.warn('[useProgramDashboards] Query error:', error.message);
        setLoading(false);
        return;
      }

      if (!data || data.length === 0) {
        // No approved/active program roles found
        setDashboards([]);
        setLoading(false);
        return;
      }

      // Program roles found

      const entries: ProgramDashboardEntry[] = [];

      for (const profile of data) {
        const handle = resolveHandle(profile.program_type);
        switch (profile.program_type) {
          case 'connector':
            entries.push({
              label: 'Connector Dashboard',
              route: '/connector/dashboard',
              programType: 'connector',
              icon: 'link',
              status: profile.status,
              handle,
              publicUrl: handle ? `/connector/${handle}` : null,
            });
            break;
          case 'general_agent':
            entries.push({
              label: 'General Agent Dashboard',
              route: '/general-agent/dashboard',
              programType: 'general_agent',
              icon: 'crown',
              status: profile.status,
              handle,
              publicUrl: handle ? `/general-agent/${handle}` : null,
            });
            break;
          case 'agent':
            entries.push({
              label: 'Agent Dashboard',
              route: '/agent/dashboard',
              programType: 'agent',
              icon: 'shield',
              status: profile.status,
              handle,
              publicUrl: handle ? `/agent/${handle}` : null,
            });
            break;
          case 'partner':
            entries.push({
              label: 'Partner Dashboard',
              route: '/partner/dashboard',
              programType: 'partner',
              icon: 'handshake',
              status: profile.status,
              handle,
              publicUrl: handle ? `/partner/${handle}` : null,
            });
            break;
          default:
            console.warn('[useProgramDashboards] Unknown program_type:', profile.program_type);
        }
      }

      // Dashboards resolved
      setDashboards(entries);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [authUser?.id]);

  return { dashboards, loading };
}
